import {ApplicationRef, Injectable, ViewContainerRef} from '@angular/core';
import {map, merge, Observable, of, Subject, take, tap} from 'rxjs';
import {PopupOverlayComponent} from '../components/helpers/popup-overlay/popup-overlay.component';
import {createScrollLockKey, DomService} from './utils/dom.service';
import {UtilsService} from "./utils/utils.service";

export interface PopupParams {
    component?: any;
    data?: any;
    overlay?: any;
    referenceId?: string;
}

@Injectable({
    providedIn: 'root'
})
export class PopupService {

    public destroyAllPopups$ = new Subject<void>();
    public listedPopup: any = {};

    constructor(
        private readonly domService: DomService,
        private applicationRef: ApplicationRef,
        private utilsService: UtilsService
    ) {
    }

    public openPopup({
        component,
        data,
        referenceId,
        overlay = PopupOverlayComponent
    }: PopupParams): Observable<any> {
        referenceId = data ? btoa(encodeURIComponent(this.utilsService.safeStableStringify(data))).substring(0, 100) : undefined;
        if (referenceId && this.listedPopup[referenceId]) {
            return of({ status: false });
        }
        const rootViewContainerRef = this.applicationRef.components[0].injector.get(ViewContainerRef);
        const dialogRef: any = rootViewContainerRef.createComponent(overlay);
        let lockKey: string | undefined;
        if (component) {
            lockKey = createScrollLockKey('popup');
            this.domService.lockBodyScroll(lockKey);
            (dialogRef.instance as any).component = component;
            if (dialogRef.instance.open && typeof dialogRef.instance.open === 'function') {
                dialogRef.instance.open()
            }
        }
        if (data) {
            (dialogRef.instance as any).data = data;
        }
        // Shared by the normal close and the forced destroyAllPopups$ sweep. Both must release the
        // scroll-lock key and the de-dupe entry: logout does not reload the page, so a half-teardown
        // leaves this root-scoped service corrupted for the rest of the tab's life.
        const teardown = () => {
            if (lockKey) {
                this.domService.unlockBodyScroll(lockKey);
            }
            if (referenceId) {
                delete this.listedPopup[referenceId];
            }
            dialogRef.destroy();
        };
        const close$ = (dialogRef.instance as any).onModalClose().pipe(
            tap((result) => {
                // Only on the close path: the overlay told us it is closing, so let it finish its
                // own bookkeeping. On the forced path we are yanking it out from under itself.
                if (dialogRef.instance.close && typeof dialogRef.instance.close === 'function') {
                    dialogRef.instance.close()
                }
                teardown();
            }),
            take(1)
        );
        if (referenceId) {
            this.listedPopup[referenceId] = close$;
        }
        return merge(
            close$,
            this.destroyAllPopups$.pipe(
                tap(teardown),
                // PopupOverlayComponent.ngOnDestroy only completes modalClose$ without emitting, so
                // without this the caller would receive `undefined` and have to guess.
                map(() => ({ status: false }))
            )
        ).pipe(
            take(1)
        );
    }
}
